import React, { useEffect, useState } from 'react'
import SummaryApi from '../common'
import { toast } from 'react-toastify'
import moment from 'moment'
import { FaEdit, FaSearch, FaEye, FaEyeSlash, FaTimes } from "react-icons/fa"

const AllCustomers = () => {
    const [allUser, setAllUsers] = useState([])
    const [searchQuery, setSearchQuery] = useState('')
    const [editModalOpen, setEditModalOpen] = useState(false)
    const [editingCustomer, setEditingCustomer] = useState(null)
    const [showPasswordInModal, setShowPasswordInModal] = useState(false)

    // For table password visibility per row
    const [visiblePasswords, setVisiblePasswords] = useState({})

    const [formData, setFormData] = useState({
        full_name: '',
        mobile_number: '',
        email: '',
        address: '',
        is_active: '1',
        password: ''
    })

    const fetchAllUsers = async () => {
        try {
            const response = await fetch(SummaryApi.allUser.url, {
                method: SummaryApi.allUser.method,
                headers: {
                    'Authorization': 'Bearer oakyaecom_no_units_916',
                    'Content-Type': 'application/json',
                },
            })

            const dataResponse = await response.json()

            if (dataResponse.status) {
                setAllUsers(dataResponse.data || [])
            } else {
                toast.error(dataResponse.message || 'Failed to load customers')
            }
        } catch (error) {
            console.error('Fetch error:', error)
            toast.error('Error fetching customers')
        }
    }

    useEffect(() => {
        fetchAllUsers()
    }, [])

    const filteredUsers = allUser.filter(user =>
        (user?.full_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (user?.mobile_number || '').includes(searchQuery) ||
        (user?.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (user?.address || '').toLowerCase().includes(searchQuery.toLowerCase())
    )

    const handleEditClick = (customer) => {
        setEditingCustomer(customer)
        setFormData({
            full_name: customer.full_name || '',
            mobile_number: customer.mobile_number || '',
            email: customer.email || '',
            address: customer.address || '',
            is_active: customer.is_active?.toString() || '1',
            password: customer.password || ''   // ← Now automatically fetch & pre-fill password
        })
        setShowPasswordInModal(false)
        setEditModalOpen(true)
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: value
        }))
    }

    const handleUpdateSubmit = async (e) => {
        e.preventDefault()

        if (!editingCustomer?.customer_id) {
            toast.error("Cannot update — missing customer ID")
            return
        }

        try {
            const payload = {
                customer_id: editingCustomer.customer_id,
                ...formData
            }

            // If password is empty → do not send it (don't change password)
            if (!payload.password?.trim()) {
                delete payload.password
            }

            const response = await fetch(SummaryApi.customerUpdate.url, {
                method: SummaryApi.customerUpdate.method,
                headers: {
                    'Authorization': 'Bearer oakyaecom_no_units_916',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            })

            const result = await response.json()

            if (result.status) {
                toast.success("Customer updated successfully")
                setEditModalOpen(false)
                setEditingCustomer(null)
                fetchAllUsers()
            } else {
                toast.error(result.message || "Failed to update customer")
            }
        } catch (err) {
            console.error("Update error:", err)
            toast.error("Error updating customer")
        }
    }

    const togglePasswordVisibility = (customerId) => {
        setVisiblePasswords(prev => ({
            ...prev,
            [customerId]: !prev[customerId]
        }))
    }

    const getDisplayPassword = (customer) => {
        const isVisible = visiblePasswords[customer.customer_id]
        if (!customer.password) return 'N/A'
        return isVisible ? customer.password : '••••••••'
    }

    const getDisplayValue = (value) => {
        if (!value) return 'N/A'
        const trimmed = String(value).trim()
        return trimmed !== '' ? trimmed : 'N/A'
    }

    return (
        <div className="min-h-screen bg-gray-50 pb-10">
            <div className='bg-white py-4 px-6 flex flex-col sm:flex-row justify-between items-center shadow-sm'>
                <h2 className='font-bold text-2xl text-gray-800'>All Customers</h2>

                <div className='relative mt-4 sm:mt-0 w-full sm:w-96'>
                    <input
                        type="text"
                        placeholder="Search by name, mobile, email or address..."
                        className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <FaSearch className="h-5 w-5 text-gray-400" />
                    </div>
                </div>
            </div>

            {allUser.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 px-4 text-center">
                    <img
                        src="https://img.freepik.com/free-vector/no-data-concept-illustration_114360-536.jpg"
                        alt="No customers found"
                        className="w-72 md:w-96 mb-8 opacity-90"
                    />
                    <h3 className="text-2xl font-semibold text-gray-700 mb-4">
                        No Customers Yet
                    </h3>
                    <p className="text-gray-500 text-lg max-w-md">
                        When users register in your store, they will appear here.
                    </p>
                </div>
            ) : (
                <div className="px-4 sm:px-6 py-6">
                    <div className="overflow-x-auto bg-white shadow-lg rounded-xl border border-gray-200">
                        <table className='min-w-full divide-y divide-gray-200'>
                            <thead className='bg-gray-100'>
                                <tr>
                                    <th className='px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>S.No</th>
                                    <th className='px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>Name</th>
                                    <th className='px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>Email</th>
                                    <th className='px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>Password</th>
                                    <th className='px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>Mobile</th>
                                    <th className='px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>Address</th>
                                    <th className='px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>Created Date & Time</th>
                                    <th className='px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>Status</th>
                                    <th className='px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>Actions</th>
                                </tr>
                            </thead>
                            <tbody className='bg-white divide-y divide-gray-200'>
                                {filteredUsers.map((el, index) => (
                                    <tr key={el.customer_id} className='hover:bg-blue-50 transition-colors'>
                                        <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900'>{index + 1}</td>
                                        <td className='px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900'>
                                            {getDisplayValue(el.full_name)}
                                        </td>
                                        <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-600'>
                                            {getDisplayValue(el.email)}
                                        </td>
                                        <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-600'>
                                            <div className="flex items-center gap-2">
                                                <span>{getDisplayPassword(el)}</span>
                                                {el.password && (
                                                    <button
                                                        onClick={() => togglePasswordVisibility(el.customer_id)}
                                                        className="text-gray-500 hover:text-gray-700"
                                                        title={visiblePasswords[el.customer_id] ? "Hide" : "Show"}
                                                    >
                                                        {visiblePasswords[el.customer_id] ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                        <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-600'>
                                            {getDisplayValue(el.mobile_number)}
                                        </td>
                                        <td className='px-6 py-4 text-sm text-gray-600'>
                                            {getDisplayValue(el.address)}
                                        </td>
                                        <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-600'>
                                            {el.created_date
                                                ? moment(el.created_date).format('MMM D, YYYY hh:mm A')
                                                : 'N/A'}
                                        </td>
                                        <td className='px-6 py-4 whitespace-nowrap'>
                                            <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                el.is_active === "1" ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                            }`}>
                                                {el.is_active === "1" ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className='px-6 py-4 whitespace-nowrap text-center'>
                                            <button
                                                onClick={() => handleEditClick(el)}
                                                className="text-blue-600 hover:text-blue-800 transition hover:scale-110"
                                                title="Edit Customer"
                                            >
                                                <FaEdit size={20} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {filteredUsers.length === 0 && searchQuery && (
                        <div className="text-center py-16 text-gray-500 text-lg">
                            No customers match your search <span className="font-medium">"{searchQuery}"</span>
                        </div>
                    )}
                </div>
            )}

            {/* Edit Modal - Two-column layout + Close (X) icon */}
            {editModalOpen && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto relative">
                        {/* Close Icon */}
                        <button
                            onClick={() => {
                                setEditModalOpen(false)
                                setEditingCustomer(null)
                                setShowPasswordInModal(false)
                            }}
                            className="absolute top-5 right-5 text-gray-600 hover:text-gray-900 transition-colors"
                            title="Close"
                        >
                            <FaTimes size={26} />
                        </button>

                        <div className="p-8 pt-14">
                            <h3 className="text-2xl font-bold text-gray-800 mb-8 text-center">Edit Customer</h3>

                            <form onSubmit={handleUpdateSubmit} className="space-y-8">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                                    {/* Left Column */}
                                    <div className="space-y-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name *</label>
                                            <input
                                                type="text"
                                                name="full_name"
                                                value={formData.full_name}
                                                disabled
                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed text-gray-700 font-medium"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Mobile Number *</label>
                                            <input
                                                type="text"
                                                name="mobile_number"
                                                value={formData.mobile_number}
                                                disabled
                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed text-gray-700 font-medium"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                                            />
                                        </div>
                                    </div>

                                    {/* Right Column */}
                                    <div className="space-y-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                                            <div className="relative">
                                                <input
                                                    type={showPasswordInModal ? "text" : "password"}
                                                    name="password"
                                                    value={formData.password}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition pr-11"
                                                    placeholder="Enter new password (optional)"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPasswordInModal(!showPasswordInModal)}
                                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 focus:outline-none"
                                                >
                                                    {showPasswordInModal ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                                                </button>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Address</label>
                                            <textarea
                                                name="address"
                                                value={formData.address}
                                                onChange={handleInputChange}
                                                rows={4}
                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-y"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                                            <div className="flex items-center gap-4">
                                                <span className={`text-sm font-medium ${formData.is_active === "1" ? "text-green-700" : "text-gray-500"}`}>
                                                    Active
                                                </span>

                                                <button
                                                    type="button"
                                                    onClick={() => setFormData(prev => ({
                                                        ...prev,
                                                        is_active: prev.is_active === "1" ? "0" : "1"
                                                    }))}
                                                    className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                        formData.is_active === "1" ? "bg-green-500" : "bg-gray-400"
                                                    }`}
                                                >
                                                    <span
                                                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-300 ${
                                                            formData.is_active === "1" ? "translate-x-7" : "translate-x-1"
                                                        }`}
                                                    />
                                                </button>

                                                <span className={`text-sm font-medium ${formData.is_active === "0" ? "text-red-700" : "text-gray-500"}`}>
                                                    Inactive
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end pt-8">
                                    <button
                                        type="submit"
                                        className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium shadow-md"
                                    >
                                        Save Changes
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AllCustomers